/**
 * Station → elevation-profile track-distance matching (AD-224 fix #1).
 *
 * The elevation profile stores waypoints as `{ name, d }` where `d` is the
 * TRACK distance (haversine along the GPS track, which underestimates official
 * trail mileage). Aid-station dots must sit at the waypoint's `d`, not at the
 * station's official mileage, or they land at the wrong spot on the curve.
 *
 * The problem this fixes: matching stations to waypoints by NAME is fragile.
 * CalTopo waypoint names are decorated with the official mile and crew tags, e.g.
 * "M 58.6 - no-Crew: Our Lady", while the DB station is "Our Lady of the Rockies".
 * The old two-way `includes()` test fails BOTH ways there (the waypoint isn't a
 * substring of the station because of the prefix; the station isn't a substring
 * of the waypoint because it says "Our Lady", not "Our Lady of the Rockies"), so
 * Our Lady fell back to its official mileage on a track-distance axis and drew in
 * the wrong place.
 *
 * The robust signal: CalTopo waypoint names reliably embed the official mile
 * ("M 58.6 ..."), and stations carry that same official mile. So we match by
 * NEAREST embedded mile first, and only fall back to name-contains when a
 * waypoint has no parseable mile or a station has no mileage.
 */

export interface ProfileWaypoint {
  name: string;
  /** Track distance (miles) along the GPS track. */
  d: number;
}

export interface StationLike {
  id: string;
  name: string;
  /** Official course mileage; null for e.g. a Start Line. */
  mileage: number | null;
}

/**
 * Parse the official mile a CalTopo waypoint name leads with, e.g.
 * "M 58.6 - no-Crew: Our Lady" → 58.6. Returns null when there is no "M <num>"
 * token (so the caller can fall back to name matching).
 *
 * Requires the "M" to start a word so it can't grab the digits out of an
 * arbitrary word that merely contains an "m".
 */
export function parseWaypointMile(name: string): number | null {
  const match = /(?:^|\s)M\s*(\d+(?:\.\d+)?)/i.exec(name);
  if (!match) return null;
  const mile = Number(match[1]);
  return Number.isFinite(mile) ? mile : null;
}

/** Case-insensitive two-way substring test (the legacy name-match fallback). */
function nameContainsMatch(stationName: string, waypointName: string): boolean {
  const s = stationName.toLowerCase();
  const w = waypointName.toLowerCase();
  return w.includes(s) || s.includes(w);
}

/**
 * How close (in official miles) an embedded-mile match may be. Station mileage
 * and the waypoint's stamped mile come from the same source data and normally
 * agree to a tenth; 1.0 tolerates minor rounding/relabeling without letting a
 * station snap to a different, genuinely distant aid station.
 */
const MILE_MATCH_TOLERANCE = 1.0;

/**
 * Build a map from station id → track distance (`d`) on the elevation profile.
 * A station with no confident match is simply absent — the caller then falls
 * back to the station's own mileage.
 */
export function buildStationTrackDistances(
  stations: StationLike[],
  waypoints: ProfileWaypoint[],
): Map<string, number> {
  const map = new Map<string, number>();

  // Precompute each waypoint's embedded official mile once.
  const waypointMiles = waypoints.map(w => ({ wp: w, mile: parseWaypointMile(w.name) }));

  for (const station of stations) {
    // 1) Preferred: nearest embedded official mile within tolerance.
    if (station.mileage != null) {
      let best: { d: number; diff: number } | null = null;
      for (const { wp, mile } of waypointMiles) {
        if (mile == null) continue;
        const diff = Math.abs(mile - station.mileage);
        if (diff <= MILE_MATCH_TOLERANCE && (best === null || diff < best.diff)) {
          best = { d: wp.d, diff };
        }
      }
      if (best) {
        map.set(station.id, best.d);
        continue;
      }
    }

    // 2) Fallback: two-way name contains (handles stations with no mileage or
    //    waypoints with no "M <mile>" stamp).
    const named = waypoints.find(w => nameContainsMatch(station.name, w.name));
    if (named) {
      map.set(station.id, named.d);
    }
  }

  return map;
}

/**
 * A track distance divided by its official mileage, for one waypoint ↔ station
 * pairing, is only believable inside this range.
 *
 * WHY: the track distance is measured along straight lines between GPS points,
 * so it cuts corners and comes out a little SHORTER than the official trail
 * mileage (ratio just under 1). It is never meaningfully LONGER. A ratio well
 * above 1 means the waypoint was paired with the wrong station; the classic case
 * is a loop course whose waypoint "Cornet Aid Station" sits on the 2nd pass (track
 * mile 20.5) but name-matches the 1st-pass station "Cornet" (mile 14.25), ratio
 * 1.44. The upper bound leaves 10% slack for GPS jitter; the lower bound throws
 * out absurd pairings at the other extreme.
 */
const PLAUSIBLE_TRACK_RATIO = { min: 0.5, max: 1.1 };

/**
 * Where the FINISH line sits on the elevation chart's x-axis, in track-distance units.
 *
 * WHY THIS EXISTS: a course's GPS track sometimes ends before the finish (e.g.
 * the track stops at Moulton, mile ~90, but the finish is at 100.1). The chart
 * then extends its x-axis to the finish and draws a flat line for the missing
 * stretch. To place the finish in TRACK units, we convert its official mileage
 * with a calibration ratio (track distance ÷ official mile) taken from the
 * furthest waypoint we can trust.
 *
 * HOW: walk the waypoints from the end of the track backwards. For each one,
 * find its station: by the mile embedded in the waypoint name ("M 58.6 …") if
 * it has one, otherwise by name. Use the FIRST pairing whose ratio is plausible
 * (see PLAUSIBLE_TRACK_RATIO); an implausible one (a wrong loop pass) is skipped
 * instead of stretching the whole axis. With no trustworthy pairing, the axis
 * simply ends where the track ends.
 *
 * The result is never less than the track's own end, so the line never runs
 * past the finish dot.
 *
 * @param trackEnd      Track distance of the last elevation point.
 * @param finishMileage Official mileage of the finish station (null/≤0 = unknown).
 */
export function finishTrackDistance(
  trackEnd: number,
  finishMileage: number | null | undefined,
  stations: StationLike[],
  waypoints: ProfileWaypoint[],
): number {
  if (finishMileage == null || finishMileage <= 0) return trackEnd;

  const byFurthest = [...waypoints].sort((a, b) => b.d - a.d);
  for (const wp of byFurthest) {
    if (wp.d <= 0) continue; // the start waypoint can't calibrate anything

    // Candidate stations for this waypoint: embedded-mile match first (the
    // reliable signal), name-contains as the fallback.
    const mile = parseWaypointMile(wp.name);
    const candidates =
      mile != null
        ? stations.filter(s => s.mileage != null && Math.abs(s.mileage - mile) <= MILE_MATCH_TOLERANCE)
        : stations.filter(s => nameContainsMatch(s.name, wp.name));

    for (const station of candidates) {
      if (station.mileage == null || station.mileage <= 0) continue;
      const ratio = wp.d / station.mileage;
      if (ratio >= PLAUSIBLE_TRACK_RATIO.min && ratio <= PLAUSIBLE_TRACK_RATIO.max) {
        return Math.max(trackEnd, finishMileage * ratio);
      }
    }
  }
  return trackEnd;
}
